/**
 * Connection Manager Service
 * 
 * Manages socket connection state and sync operations:
 * - Tracks online/offline status (browser + socket)
 * - Processes action queue on reconnection (trip status changes, etc.)
 * - Prioritizes critical actions during sync
 * - Server-wins conflict resolution (fresh data replaces optimistic)
 * 
 * NOTE: GPS updates are fire-and-forget (not queued) since server doesn't ACK them.
 * 
 * @module services/connectionManager
 */

import { offlineStorage, ActionPriority } from './offlineStorage';

// Sync configuration
const SYNC_CONFIG = {
  // Delay before starting sync after reconnect (ms)
  RECONNECT_SYNC_DELAY: 1000,
  
  // Max retries per action before giving up
  MAX_RETRIES: 3,
  
  // Delay between retry attempts (ms)
  RETRY_DELAY: 2000,
};

/**
 * Connection Manager Class
 * Coordinates socket state and offline sync
 */
class ConnectionManagerService {
  constructor() {
    this.socket = null;
    this.isOnline = navigator.onLine;
    this.isSocketConnected = false;
    this.isSyncing = false;
    this.syncAborted = false;
    this.listeners = [];
    
    // Bind browser online/offline events
    window.addEventListener('online', () => this.handleBrowserOnline());
    window.addEventListener('offline', () => this.handleBrowserOffline());
  }

  /**
   * Initialize with a socket instance
   * @param {Object} socket - Socket.IO instance
   */
  setSocket(socket) {
    this.socket = socket;
    
    // Socket event handlers
    socket.on('connect', () => {
      console.log('[ConnectionManager] Socket connected');
      this.isSocketConnected = true;
      this.notifyListeners();
      
      // Start sync after brief delay (let socket stabilize)
      setTimeout(() => {
        this.syncPendingActions();
      }, SYNC_CONFIG.RECONNECT_SYNC_DELAY);
    });

    socket.on('disconnect', (reason) => {
      console.log('[ConnectionManager] Socket disconnected:', reason);
      this.isSocketConnected = false;
      this.notifyListeners();
    });

    socket.on('reconnecting', (attemptNumber) => {
      console.log('[ConnectionManager] Socket reconnecting, attempt:', attemptNumber);
      this.notifyListeners();
    });

    socket.on('reconnect', () => {
      console.log('[ConnectionManager] Socket reconnected');
      this.isSocketConnected = true;
      this.notifyListeners();
      
      // Sync pending actions
      setTimeout(() => {
        this.syncPendingActions();
      }, SYNC_CONFIG.RECONNECT_SYNC_DELAY);
    });

    socket.on('reconnect_failed', () => {
      console.log('[ConnectionManager] Socket reconnect failed');
      this.isSocketConnected = false;
      this.notifyListeners();
    });

    // Initial state check
    this.isSocketConnected = socket.connected;
  }

  /**
   * Check if fully online (browser + socket)
   * @returns {boolean}
   */
  get online() {
    return this.isOnline && this.isSocketConnected;
  }

  /**
   * Handle browser coming online
   */
  handleBrowserOnline() {
    console.log('[ConnectionManager] Browser online');
    this.isOnline = true;
    
    // Try to reconnect socket if not connected
    if (this.socket && !this.socket.connected) {
      this.socket.connect();
    }
    
    this.notifyListeners();
  }

  /**
   * Handle browser going offline
   */
  handleBrowserOffline() {
    console.log('[ConnectionManager] Browser offline');
    this.isOnline = false;
    this.notifyListeners();
  }

  /**
   * Subscribe to connection state changes
   * @param {Function} callback - Called with { online, syncing, pendingCount }
   * @returns {Function} Unsubscribe function
   */
  onStateChange(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  /**
   * Notify listeners of state change
   */
  notifyListeners() {
    const state = {
      online: this.online,
      browserOnline: this.isOnline,
      socketConnected: this.isSocketConnected,
      syncing: this.isSyncing,
      pendingCount: offlineStorage.getPendingCount(),
      criticalPending: offlineStorage.getCriticalPendingCount(),
    };
    
    this.listeners.forEach(cb => {
      try {
        cb(state);
      } catch (e) {
        console.error('[ConnectionManager] Listener error:', e);
      }
    });
  }

  // ============================================================================
  // ACTION SYNC
  // ============================================================================

  /**
   * Sync all pending actions to server
   * Processes in priority order: CRITICAL → HIGH → NORMAL → LOW
   */
  async syncPendingActions() {
    if (this.isSyncing || !this.online) {
      console.log('[ConnectionManager] Skipping sync:', this.isSyncing ? 'already syncing' : 'offline');
      return;
    }

    this.isSyncing = true;
    this.syncAborted = false;
    this.notifyListeners();

    const pending = offlineStorage.getPendingActions();
    console.log(`[ConnectionManager] Starting sync of ${pending.length} pending actions`);

    let syncedCount = 0;
    let failedCount = 0;

    for (const action of pending) {
      // Check if we went offline during sync
      if (!this.online || this.syncAborted) {
        console.log('[ConnectionManager] Sync aborted - connection lost');
        break;
      }

      // Remove permanently failed actions (exceeded max retries)
      if (action.retryCount >= SYNC_CONFIG.MAX_RETRIES) {
        console.warn(`[ConnectionManager] Action ${action.id} exceeded max retries, removing permanently`);
        offlineStorage.removeAction(action.id);
        failedCount++;
        continue;
      }

      try {
        await this.sendAction(action);
        offlineStorage.removeAction(action.id);
        syncedCount++;
        console.log(`[ConnectionManager] Synced action: ${action.type} (${action.id})`);
      } catch (error) {
        console.error(`[ConnectionManager] Failed to sync action ${action.id}:`, error);
        offlineStorage.updateActionRetry(action.id, error.message);
        failedCount++;
        
        // Wait before next retry
        await this.delay(SYNC_CONFIG.RETRY_DELAY);
      }

      // Update listeners with progress
      this.notifyListeners();
    }

    this.isSyncing = false;
    offlineStorage.updateLastSync();
    this.notifyListeners();

    console.log(`[ConnectionManager] Sync complete: ${syncedCount} synced, ${failedCount} failed`);

    // Clear optimistic flag - server data is authoritative (server-wins)
    offlineStorage.clearOptimisticFlag();
  }

  /**
   * Send a single action to server
   * @param {Object} action - Queued action
   * @returns {Promise} Resolves on success, rejects on failure
   */
  sendAction(action) {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.socket.connected) {
        reject(new Error('Socket not connected'));
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('Action timeout'));
      }, 10000); // 10 second timeout

      // Handle different action types
      // NOTE: GPS_UPDATE is not queued - it's fire-and-forget since server doesn't ACK
      if (action.type === 'Message' && action.messageFormat) {
        // Legacy pipe-delimited message format
        // Include original timestamp in the message for server-side recording
        this.socket.emit('Message', action.messageFormat, (response) => {
          clearTimeout(timeout);
          if (response?.error) {
            reject(new Error(response.error));
          } else {
            resolve(response);
          }
        });
      } else {
        // Generic socket emit with callback
        // Include original timestamp for server to use for recording
        const dataWithTimestamp = {
          ...action.data,
          _originalTimestamp: action.timestamp, // When action actually occurred
          _queuedAt: action.queuedAt,
        };
        
        this.socket.emit(action.type, dataWithTimestamp, (response) => {
          clearTimeout(timeout);
          if (response?.error) {
            reject(new Error(response.error));
          } else {
            resolve(response);
          }
        });
      }
    });
  }

  /**
   * Abort current sync (e.g., when user logs out)
   */
  abortSync() {
    this.syncAborted = true;
  }

  /**
   * Clear all pending actions (manual recovery for stuck queue)
   * Use this when actions cannot be retried and need to be abandoned
   */
  clearAllPendingActions() {
    console.warn('[ConnectionManager] Clearing all pending actions (manual recovery)');
    offlineStorage.clearPendingActions();
    this.notifyListeners();
  }

  /**
   * Get summary of pending actions for debugging
   * @returns {Object} Summary with counts and oldest action
   */
  getPendingActionsSummary() {
    const pending = offlineStorage.getPendingActions();
    
    const failedActions = pending.filter(a => a.retryCount >= SYNC_CONFIG.MAX_RETRIES);
    const retriableActions = pending.filter(a => a.retryCount < SYNC_CONFIG.MAX_RETRIES);
    
    return {
      total: pending.length,
      failed: failedActions.length,
      retriable: retriableActions.length,
      oldestAction: pending.length > 0 ? pending[0] : null,
    };
  }

  // ============================================================================
  // IMMEDIATE SEND (Online Mode)
  // ============================================================================

  /**
   * Send action immediately if online, queue if offline
   * 
   * @param {string} type - Action type
   * @param {*} data - Action data
   * @param {Object} options - Options
   * @param {string} options.messageFormat - For legacy Message events
   * @param {Function} options.onOptimisticUpdate - Called to apply optimistic UI update
   * @param {Function} options.onSuccess - Called on successful send
   * @param {Function} options.onQueued - Called if action was queued
   * @returns {Promise} Resolves with response or queue result
   */
  async sendOrQueue(type, data, options = {}) {
    const { messageFormat, onOptimisticUpdate, onSuccess, onQueued } = options;
    const timestamp = Date.now();

    // Apply optimistic update immediately (for UI responsiveness)
    if (onOptimisticUpdate) {
      onOptimisticUpdate(data);
    }

    // If online, try to send immediately
    if (this.online) {
      try {
        const action = { type, data, messageFormat, timestamp };
        const response = await this.sendAction(action);
        
        if (onSuccess) onSuccess(response);
        return { sent: true, response };
      } catch (error) {
        console.warn(`[ConnectionManager] Immediate send failed, queueing: ${error.message}`);
        // Fall through to queue
      }
    }

    // Queue for later sync
    const actionId = offlineStorage.queueAction({ type, data, messageFormat });
    this.notifyListeners();
    
    if (onQueued) onQueued(actionId);
    return { sent: false, queued: true, actionId };
  }

  // ============================================================================
  // SERVER-WINS CONFLICT RESOLUTION
  // ============================================================================

  /**
   * Handle fresh data from server (server-wins)
   * Replaces any optimistic local data
   * 
   * @param {Object} serverData - Fresh route data from server
   */
  handleServerData(serverData) {
    console.log('[ConnectionManager] Received server data (server-wins)');
    
    // Save server data, clearing any optimistic state
    offlineStorage.saveRouteData(serverData);
    offlineStorage.clearOptimisticFlag();
    
    this.notifyListeners();
  }

  // ============================================================================
  // UTILITIES
  // ============================================================================

  /**
   * Simple delay helper
   * @param {number} ms - Milliseconds
   * @returns {Promise}
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get current state snapshot
   * @returns {Object}
   */
  getState() {
    return {
      online: this.online,
      browserOnline: this.isOnline,
      socketConnected: this.isSocketConnected,
      syncing: this.isSyncing,
      pendingCount: offlineStorage.getPendingCount(),
      criticalPending: offlineStorage.getCriticalPendingCount(),
      lastSync: offlineStorage.getLastSync(),
      diagnostics: offlineStorage.getDiagnostics(),
    };
  }

  /**
   * Force manual reconnection attempt
   */
  reconnect() {
    if (this.socket && !this.socket.connected) {
      console.log('[ConnectionManager] Manual reconnect requested');
      this.socket.connect();
    }
  }

  /**
   * Force immediate sync (for manual trigger)
   */
  forceSync() {
    if (this.online && !this.isSyncing) {
      this.syncPendingActions();
    }
  }
}

// Export singleton instance
export const connectionManager = new ConnectionManagerService();

// Also export the class for testing
export { ConnectionManagerService, SYNC_CONFIG };
